// Data-viz tokens for the cohort analytics dashboards.
//
// These are deliberately NOT the MUI theme palette. The app theme is a brand
// palette (primary #2196f3, secondary #f50057) chosen for buttons and chips,
// where only contrast against the surface matters. Charts encode meaning in
// colour, so they need a palette that survives colour-vision deficiency when
// two marks sit side by side — which the brand pair does not.
//
// Every set below was checked with the data-viz palette validator against the
// surface these charts actually render on (MUI Paper, #ffffff):
//
//   categorical (all-pairs) .. PASS  worst CVD dE 9.2, normal-vision dE 24.0
//   ordinal ramp ............. PASS  monotone L, all adjacent gaps >= 0.06
//   diverging poles .......... PASS  worst CVD dE 23.8, normal-vision dE 31.6
//
// If you change a hex here, re-run the validator before shipping.

// Categorical identity. Capped at three: past three slots the full set cannot
// clear the all-pairs floors, and scatter/bubble charts put every pair on
// screen at once. A fourth series folds into "Other" or becomes a facet.
export const SERIES = {
  blue: '#2a78d6',
  orange: '#eb6834',
  aqua: '#1baf7a',
};
export const SERIES_ORDER = [SERIES.blue, SERIES.orange, SERIES.aqua];

// Aqua measures 2.82:1 against white — under the 3:1 bar. The relief rule
// applies wherever it is used: ship a visible label or the table view, never
// let the hue carry the value alone.
export const NEEDS_LABEL_RELIEF = [SERIES.aqua];

// Continuous magnitude (heatmap cells). One hue, light -> dark.
export const SEQUENTIAL = [
  '#cde2fb', '#b7d3f6', '#9ec5f4', '#86b6ef', '#6da7ec',
  '#5598e7', '#3987e5', '#2a78d6', '#256abf', '#1c5cab',
  '#184f95', '#104281', '#0d366b',
];

// Discrete ordered stages (funnel, pipeline). Wider steps than the sequential
// ramp so adjacent stages stay separable; light end still clears the surface.
export const ORDINAL_5 = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'];

// Polarity: above/below a reference. Neutral grey midpoint means "no gap" —
// never a hue at the middle, or the zero point reads as a value.
export const DIVERGING = {
  positive: '#2a78d6',
  negative: '#d03b3b',
  neutral: '#f0efec',
};

// Reserved for state. Never reused as "series 4". Always shipped with an icon
// and a word — these four do not clear the categorical CVD gate against each
// other (red vs green measures dE 4.1 under deuteranopia), so colour alone is
// never sufficient here by construction.
export const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
};

// Chart chrome. Recessive by design: the data is the only loud thing.
export const INK = {
  primary: '#0b0b0b',
  secondary: '#52514e',
  muted: '#898781',
  grid: '#e1e0d9',
  axis: '#c3c2b7',
  surface: '#ffffff',
  plane: '#f9f9f7',
};

// Everything that is not the point of the chart. Pairs with one accent colour
// for the "highlight one, grey the rest" pattern.
export const MUTED_MARK = '#d8d7d0';

export const AXIS_TICK = { fontSize: 12, fill: INK.muted };
export const AXIS_LABEL = { fontSize: 12, fill: INK.secondary, fontWeight: 600 };

// Mark geometry, kept in one place so every chart reads as one system.
export const MARK = {
  barRadius: 4,
  barSize: 18,
  strokeWidth: 2,
  dotRadius: 7,
  dotRing: 2,
  gap: 2,
};

export const sequentialStep = (ratio) => {
  if (!Number.isFinite(ratio)) return INK.grid;
  const clamped = Math.max(0, Math.min(1, ratio));
  const index = Math.round(clamped * (SEQUENTIAL.length - 1));
  return SEQUENTIAL[index];
};

// Text that has to sit on top of a sequential cell. The ramp crosses from
// light to dark, so the ink has to cross with it or the deep end goes
// unreadable — this is the contrast relief for the heatmap.
export const inkOnSequential = (ratio) => (
  Number.isFinite(ratio) && ratio > 0.55 ? '#ffffff' : INK.primary
);

export const statusForScore = (percent) => {
  if (percent == null || !Number.isFinite(percent)) return 'unknown';
  if (percent >= 75) return 'good';
  if (percent >= 60) return 'warning';
  if (percent >= 40) return 'serious';
  return 'critical';
};
